import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import { ConfirmationDialog } from '../ui/confirmation-dialog'
import { Loader2, Building2 } from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { DateTime } from 'luxon'

import { useClientStore } from '../../stores/useClientStore'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { useSessionStore } from '../../stores/useSessionStore'
import { ClientRequest, ClientRequestFromClient } from '../../requests/ClientRequest'
import {
  BadgeConfigurationRequest,
  DefaultBadgeConfigurationRequest,
} from '../../requests/BadgeConfigurationRequest'
import { DefaultTimezone } from '../../common/Timezones'
import ClientEndpoints from '../../endpoints/ClientEndpoints'

import { ClientOverviewSection } from './sections/ClientOverviewSection'
import { ClientBadgeSection } from './sections/ClientBadgeSection'
import { ClientEventsSection } from './sections/ClientEventsSection'

export const ClientDetailPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const isCreateMode = !clientId

  const { setClientId, clearClient, refresh, createOrUpdate, client } = useClientStore()
  const { regions, refreshRegions, refreshClients } = useApplicationStore()
  const { hasAdmin } = useSessionStore()

  const [loading, setLoading] = useState(!isCreateMode)
  const [isSavingOverview, setIsSavingOverview] = useState(false)
  const [isSavingBadge, setIsSavingBadge] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)

  const [formData, setFormData] = useState<ClientRequest>({
    timezone: DefaultTimezone,
    name: '',
    workRegionId: -1,
    notifyOnNewRole: '',
    clientBadgesEnabled: false,
    badgeConfiguration: DefaultBadgeConfigurationRequest(),
  })

  // Load regions if not already loaded
  useEffect(() => {
    if (!regions || regions.length === 0) {
      refreshRegions().catch(console.error)
    }
  }, [regions, refreshRegions])

  // Load client data or clear for create mode
  useEffect(() => {
    if (isCreateMode) {
      clearClient()
      setFormData({
        timezone: DefaultTimezone,
        name: '',
        workRegionId: regions && regions.length > 0 ? regions[0].id : -1,
        notifyOnNewRole: '',
        clientBadgesEnabled: false,
        badgeConfiguration: DefaultBadgeConfigurationRequest(),
      })
      setLoading(false)
    } else {
      const loadClient = async () => {
        setLoading(true)
        try {
          setClientId(parseInt(clientId))
          const fullClient = await refresh()
          if (fullClient) {
            setFormData(ClientRequestFromClient(fullClient))
          }
        } catch (error) {
          console.error('Failed to load client:', error)
          toast.error(extractErrorMessage(error, 'Failed to load client data'))
        } finally {
          setLoading(false)
        }
      }
      loadClient()
    }
  }, [clientId, isCreateMode])

  // Update default region once regions load (create mode only)
  useEffect(() => {
    if (isCreateMode && regions && regions.length > 0 && formData.workRegionId === -1) {
      setFormData((prev) => ({ ...prev, workRegionId: regions[0].id }))
    }
  }, [regions, isCreateMode, formData.workRegionId])

  const handleFieldChange = (field: keyof ClientRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const handleBadgeConfigChange = (field: keyof BadgeConfigurationRequest, value: any) => {
    setFormData((prev) => ({
      ...prev,
      badgeConfiguration: {
        ...prev.badgeConfiguration!,
        [field]: value,
      },
    }))
  }

  const validateOverview = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {
      newErrors.name = 'A name is required for the client.'
    }
    if (formData.workRegionId === -1) {
      newErrors.workRegionId = 'A work region is required for the client.'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSaveOverview = async () => {
    if (!validateOverview()) return

    setIsSavingOverview(true)
    try {
      const result = await createOrUpdate(formData)
      await refreshClients()
      toast.success(isCreateMode ? 'Client created successfully' : 'Client updated successfully')
      if (isCreateMode && result?.id) {
        navigate(`/client/${result.id}`, { replace: true })
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save client'))
    } finally {
      setIsSavingOverview(false)
    }
  }

  const handleSaveBadge = async () => {
    setIsSavingBadge(true)
    try {
      await createOrUpdate(formData)
      await refreshClients()
      toast.success('Badge configuration saved')
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save badge configuration'))
    } finally {
      setIsSavingBadge(false)
    }
  }

  const handleArchive = async () => {
    if (!client) return

    setIsArchiving(true)
    try {
      await ClientEndpoints.setClientArchivedStatus(client.id, {
        field: 'archived',
        value: '' + !client.dateArchived,
      })
      await refresh()
      await refreshClients()
      toast.success(client.dateArchived ? 'Client unarchived' : 'Client archived')
      setShowArchiveDialog(false)
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update archive status'))
    } finally {
      setIsArchiving(false)
    }
  }

  const formatDate = (date: Date | string | number | undefined) => {
    if (!date) return '-'
    try {
      let dateObj: DateTime
      if (typeof date === 'number') {
        dateObj = DateTime.fromMillis(date)
      } else if (typeof date === 'string') {
        dateObj = DateTime.fromISO(date)
      } else {
        dateObj = DateTime.fromJSDate(date)
      }
      return dateObj.isValid ? dateObj.toFormat('MMM dd, yyyy') : '-'
    } catch {
      return '-'
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">Loading client...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!isCreateMode && !client) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <p className="text-muted-foreground">Client not found</p>
        </div>
      </div>
    )
  }

  const isAdmin = hasAdmin()
  const pageTitle = isCreateMode ? 'New Client' : client?.name || 'Client'

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/client">Clients</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-semibold">{pageTitle}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Building2 className="h-6 w-6" />
                {pageTitle}
              </CardTitle>
              {!isCreateMode && client && (
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span>Created: {formatDate(client.dateCreated)}</span>
                  <Badge variant={client.dateArchived ? 'destructive' : 'default'}>
                    {client.dateArchived ? 'Archived' : 'Active'}
                  </Badge>
                </div>
              )}
            </div>
            {!isCreateMode && isAdmin && client && (
              <Button
                variant="outline"
                onClick={() => setShowArchiveDialog(true)}
                className={client.dateArchived ? '' : 'text-destructive hover:text-destructive'}
              >
                {client.dateArchived ? 'Unarchive' : 'Archive'}
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Client Overview Section */}
      <ClientOverviewSection
        formData={formData}
        onChange={handleFieldChange}
        onSave={handleSaveOverview}
        errors={errors}
        isSaving={isSavingOverview}
        isAdmin={isAdmin}
        regions={regions || []}
      />

      {/* Badge Configuration Section */}
      <ClientBadgeSection
        formData={formData}
        onChange={handleFieldChange}
        onBadgeConfigChange={handleBadgeConfigChange}
        onSave={handleSaveBadge}
        isSaving={isSavingBadge}
        isAdmin={isAdmin}
        clientId={client?.id}
      />

      {/* Associated Events (edit mode only) */}
      {!isCreateMode && client && (
        <ClientEventsSection events={client.events || []} />
      )}

      {/* Archive Confirmation Dialog */}
      {client && (
        <ConfirmationDialog
          open={showArchiveDialog}
          onOpenChange={setShowArchiveDialog}
          type={client.dateArchived ? 'unarchive' : 'archive'}
          title={client.dateArchived ? 'Unarchive Client' : 'Archive Client'}
          itemName={client.name}
          description={
            client.dateArchived
              ? `Are you sure you want to unarchive "${client.name}"? This will make the client active and available for new events.`
              : `Are you sure you want to archive "${client.name}"?`
          }
          warningContent={
            !client.dateArchived
              ? '<strong>Warning:</strong> Archived clients will no longer be available for new events, but existing events and data will remain intact.'
              : undefined
          }
          confirmText={client.dateArchived ? 'Unarchive Client' : 'Archive Client'}
          isLoading={isArchiving}
          onConfirm={handleArchive}
          onCancel={() => setShowArchiveDialog(false)}
        />
      )}
    </div>
  )
}
